<template>
  <TransitionGroup tag="ul" class="icon-list" :name="transitionName" :css="transitionCss">
    <li v-for="(item, idx) in visibleItems" :key="item.id" class="icon-list-row">
      <div
        class="icon-list-item"
        :class="[{ 'body-below-icon': showBodyBelow, wrapped: fields.wrapItem }]"
      >
        <div
          class="icon-heading-row"
          :class="[`side-${effectiveIconSide}`, `align-${effectiveIconAlign}`]"
        >
          <!-- Progress-sequence marker (opt-in, ring theme only). Replaces the
               image/numeric/symbol marker for the item; built-ins are
               overridable via slots. The bar theme renders no marker column
               at all — see the text-column below instead. -->
          <template v-if="progressMode && progressStyle === 'ring'">
            <!-- Authoring (editor at rest): expose the override drop-zones on the
                 first row so they can be filled; built-in checkmark elsewhere. -->
            <template v-if="!animLive">
              <div v-if="idx === 0" class="progress-marker progress-marker--authoring">
                <!-- Wrapped in IconListItemScope (with representative values) so
                     a TipTap dropped into the slot can interpolate {{comp.*}}
                     while authoring, not only at runtime. -->
                <IconListItemScope :progress="65" state="filling">
                  <RenderSlot
                    name="progressIcon"
                    :children="slotChildren?.progressIcon"
                    :editable="editable"
                  >
                    <ProgressRingShape
                      :progress="65"
                      :size="progressIconSizeCss"
                      :stroke-width="progressStrokeWidthVal"
                      :track-color="css.progressTrack"
                      :fill-color="css.progressFill"
                    />
                  </RenderSlot>
                </IconListItemScope>
                <IconListItemScope :progress="100" state="done">
                  <RenderSlot
                    name="checkmarkIcon"
                    :children="slotChildren?.checkmarkIcon"
                    :editable="editable"
                  >
                    <ProgressCheck :size="progressIconSizeCss" :color="css.checkmark" />
                  </RenderSlot>
                </IconListItemScope>
              </div>
              <span v-else class="progress-marker">
                <ProgressCheck :size="progressIconSizeCss" :color="css.checkmark" />
              </span>
            </template>
            <!-- Animated (runtime, or editor preview): per-item ring → checkmark,
                 with optional slotted overrides driven via IconListItemScope. -->
            <template v-else>
              <span v-if="itemState(idx) === 'done'" class="progress-marker">
                <IconListItemScope v-if="hasCheckmarkSlot" :progress="100" state="done">
                  <RenderComponents
                    :schema="{ children: slotChildren!.checkmarkIcon! }"
                    :ComponentRegistry="componentRegistry"
                    :editable="false"
                  />
                </IconListItemScope>
                <ProgressCheck v-else :size="progressIconSizeCss" :color="css.checkmark" />
              </span>
              <span v-else class="progress-marker">
                <IconListItemScope
                  v-if="hasProgressSlot"
                  :progress="itemProgress[idx] ?? 0"
                  :state="itemState(idx)"
                >
                  <RenderComponents
                    :schema="{ children: slotChildren!.progressIcon! }"
                    :ComponentRegistry="componentRegistry"
                    :editable="false"
                  />
                </IconListItemScope>
                <ProgressRingShape
                  v-else
                  :progress="itemProgress[idx] ?? 0"
                  :size="progressIconSizeCss"
                  :stroke-width="progressStrokeWidthVal"
                  :track-color="css.progressTrack"
                  :fill-color="css.progressFill"
                />
              </span>
            </template>
          </template>

          <!-- Default markers — plain list (progressMode off). The bar theme
               (progressMode && progressStyle === 'bar') renders neither this
               nor the ring block above: no marker column at all. -->
          <template v-else-if="!progressMode">
            <PlainImage
              v-if="markerType === 'image'"
              :image="toImageData(item.icon)"
              :skeleton-color="imageSkeletonBgColor"
              :shimmer-color="imageSkeletonShimmerColor"
              class="icon-img"
            />
            <div v-else-if="markerType === 'numeric'" class="icon-marker">
              {{ itemNumber(item) }}.
            </div>
            <div v-else class="icon-marker">{{ markerSymbol }}</div>
          </template>

          <div class="text-column">
            <!-- Bar theme: heading shares its row with the live percentage,
                 followed by the fill track. Duplicated (rather than wrapping
                 the plain heading-text block below) because the two are
                 mutually-exclusive siblings, not layered. -->
            <template v-if="progressMode && progressStyle === 'bar'">
              <div
                class="heading-percent-row"
                :class="{ 'is-pending': barItemState(idx) === 'pending' }"
              >
                <div v-if="item.heading?.key" class="heading-text">
                  <TipTap
                    :model-value="pipe.getTranslation(item.heading.key)"
                    @update:model-value="
                      (val) => pipe.setTranslation(item.heading!.key, val as object)
                    "
                    mode="single"
                  />
                </div>
                <span class="progress-percent">{{ Math.round(barDisplayProgress(idx)) }}%</span>
              </div>
              <div
                class="progress-bar-track"
                :style="{
                  backgroundColor:
                    barItemState(idx) === 'pending' ? css.pendingBarTrack : css.progressBarTrack,
                }"
              >
                <div
                  class="progress-bar-fill"
                  :style="{ width: `${barDisplayProgress(idx)}%`, background: css.progressBarFill }"
                />
              </div>
            </template>
            <div v-else-if="item.heading?.key" class="heading-text">
              <TipTap
                :model-value="pipe.getTranslation(item.heading.key)"
                @update:model-value="(val) => pipe.setTranslation(item.heading!.key, val as object)"
                mode="single"
              />
            </div>
            <template v-if="!showBodyBelow">
              <div v-if="bodyVisible(idx) && item.body?.key" class="body-text">
                <TipTap
                  :model-value="pipe.getTranslation(item.body.key)"
                  @update:model-value="(val) => pipe.setTranslation(item.body!.key, val as object)"
                  mode="single"
                />
              </div>
              <div v-else-if="bodySkeleton(idx)" class="body-skeleton" aria-hidden="true">
                <span class="skeleton-line" />
                <span class="skeleton-line short" />
              </div>
            </template>
          </div>
        </div>

        <template v-if="showBodyBelow">
          <div v-if="bodyVisible(idx) && item.body?.key" class="body-text body-below">
            <TipTap
              :model-value="pipe.getTranslation(item.body.key)"
              @update:model-value="(val) => pipe.setTranslation(item.body!.key, val as object)"
              mode="single"
            />
          </div>
          <div v-else-if="bodySkeleton(idx)" class="body-skeleton body-below" aria-hidden="true">
            <span class="skeleton-line" />
            <span class="skeleton-line short" />
          </div>
        </template>
      </div>
    </li>
  </TransitionGroup>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { PipeDisplayMode, usePipe } from '@render/composables/usePipe'
import { useComponentProcessors } from '@render/composables/useComponentProcessors'
import { useEditorCommands } from '@render/composables/useEditorCommands'
import {
  getColorStyleData,
  getGradientStyleData,
  getSizeStyleData,
  getSpacingStyleData,
} from '@render/composables/useStyleData'
import { useDirection } from '@render/composables/useDirection'
import TipTap from '@render/lib/TipTap'
import PlainImage from '../../generic/PlainImage.vue'
import RenderSlot from '@render/RenderSlot.vue'
import RenderComponents from '@render/RenderComponents.vue'
import ProgressRingShape from '@render/components/generic/ProgressRingShape.vue'
import ProgressCheck from './ProgressCheck.vue'
import IconListItemScope from './IconListItemScope.vue'
import type { IconListItemState } from './progressContext'
import type { ImageFieldData } from '@render/composables/useImagePreset'
import type { ColorField, SizeField } from '@render/types/styles'
import type { ComponentNode, IconListItem, ImageFieldType } from '@api/types.gen'
import type {
  IconListFields as Model,
  AnimationTrigger,
  AnimationType,
  MarkerType,
  TextAlignOverride,
  TextStylePreset,
  ProgressStyle,
} from './definition'

type IconAlign = 'top' | 'center' | 'bottom'
// Legacy values ('heading' → 'top', 'both' → 'center') are migrated on read.
type StoredIconAlign = IconAlign | 'heading' | 'both'

type Layout = {
  iconSide: 'left' | 'right' | 'right_if_rtl'
  iconAlign: StoredIconAlign
  bodyBelowIcon: boolean
}

type TextDecoration = 'italic' | 'underline' | 'line-through'

const pipe = usePipe()
const { isRtl } = useDirection()

const fields = defineModel<Model>('fields', {
  required: true,
  default: () => ({
    items: { type: 'icon_list_items', items: [] },
    layout: { iconSide: 'left', iconAlign: 'heading', bodyBelowIcon: false },
    wrapItem: false,
  }),
})

// Authored override content per slot (grouped by RenderComponents from children
// carrying a `.slot`) + editability, threaded down from the render tree.
const props = defineProps<{
  slotChildren?: Record<string, ComponentNode[]>
  editable?: boolean
}>()

// Registry needed to render slotted override subtrees per item at runtime.
const componentRegistry = inject<Record<string, { component: unknown }>>(
  'renderComponentRegistry',
  {},
)

const items = computed<IconListItem[]>(() => fields.value.items?.items ?? [])

const layout = computed<Layout>(() => ({
  iconSide: fields.value.layout?.iconSide ?? 'left',
  iconAlign: fields.value.layout?.iconAlign ?? 'top',
  bodyBelowIcon: fields.value.layout?.bodyBelowIcon ?? false,
}))

const effectiveIconAlign = computed<IconAlign>(() => {
  const v = layout.value.iconAlign
  if (v === 'heading') return 'top'
  if (v === 'both') return 'center'
  return v
})

const effectiveIconSide = computed<'left' | 'right'>(() => {
  if (layout.value.iconSide === 'left') return 'left'
  if (layout.value.iconSide === 'right') return 'right'
  // 'right_if_rtl'
  return isRtl.value ? 'right' : 'left'
})

const showBodyBelow = computed(() => !!layout.value.bodyBelowIcon)

const markerType = computed<MarkerType>(() => fields.value.markerType ?? 'image')
const markerSymbol = computed(() => fields.value.markerSymbol ?? '•')

function resolveTextAlign(v: TextAlignOverride | undefined): string {
  if (!v || v === 'auto') return effectiveIconSide.value === 'right' ? 'end' : 'start'
  return v
}

const headingTextAlign = computed(() => resolveTextAlign(fields.value.headingTextAlign))
const bodyTextAlign = computed(() => resolveTextAlign(fields.value.bodyTextAlign))

function toImageData(img: ImageFieldType | undefined): ImageFieldData | undefined {
  if (!img?.id) return undefined
  return img as ImageFieldData
}

function itemNumber(item: IconListItem): number {
  return items.value.indexOf(item) + 1
}

// Overloaded so a plain-string fallback keeps the narrow `string` return its
// callers (inline styles, child props) need, while a preset-backed fallback —
// which is null under the 'custom' preset — stays nullable.
function color(f: ColorField | undefined, fallback: string): string
function color(f: ColorField | undefined, fallback: string | null): string | null
function color(f: ColorField | undefined, fallback: string | null): string | null {
  return getColorStyleData(f) || fallback
}

const imageSkeletonBgColor = computed(
  () => getColorStyleData(fields.value.imageSkeletonBgColor) ?? 'transparent',
)
const imageSkeletonShimmerColor = computed(
  () => getColorStyleData(fields.value.imageSkeletonShimmerColor) ?? 'transparent',
)
function size(f: SizeField | undefined, fallback: string | null): string | null {
  return getSizeStyleData(f) || fallback
}

const animTrigger = computed<AnimationTrigger>(() => fields.value.animationTrigger ?? 'none')
const animType = computed<AnimationType>(() => fields.value.animationType ?? 'fade')
const animDelayMs = computed(() => Math.max(0, Number(fields.value.animationDelay ?? 150)))
const animDurationMs = computed(() => Math.max(0, Number(fields.value.animationDuration ?? 400)))
// Number of items at the top that are rendered immediately (no enter
// animation). Clamped to non-negative; the visibleItems and startAnimation
// paths further clamp to items.length.
const animSkipCount = computed(() => Math.max(0, Number(fields.value.animationSkipCount ?? 0)))
// TransitionGroup picks enter classes from this name. Changing it swaps
// which CSS keyframe runs per item without touching the template.
const transitionName = computed(() =>
  animType.value === 'slide' ? 'icon-list-slide' : 'icon-list-fade',
)
const isEditor = computed(() => pipe.currentMode.value === PipeDisplayMode.EDITOR)

// Animation is suppressed in EDITOR mode so authors don't see flashing on
// every field tweak and newly-added items stay visible — UNLESS the author
// explicitly triggered a preview run via the "Preview Animation" button (which
// calls startAnimation through the processor). While a preview run is active
// we let the hidden-class logic apply even in editor mode.
const previewing = ref(false)
const animLive = computed(() => !isEditor.value || previewing.value)
const effectiveTrigger = computed<AnimationTrigger>(() =>
  animLive.value ? animTrigger.value : 'none',
)
const animEnabled = computed(() => effectiveTrigger.value !== 'none')
// The staggered reveal (TransitionGroup enter classes) and progress-sequence
// mode are mutually exclusive — in progress mode every item is present from
// the start (all rings at 0%), so suppress the enter transition.
const transitionCss = computed(() => animEnabled.value && !progressMode.value)

const revealCount = ref(0)
let pendingTimers: ReturnType<typeof setTimeout>[] = []

// Only the revealed prefix is rendered while animating, so the parent
// container reflows as items appear. When animation is off (editor at rest,
// trigger='none', or after a run settles) we render the full list. The
// animationSkipCount floor means the first N items are always rendered so
// the list isn't empty on initial paint — those items sit outside the
// TransitionGroup enter animation (they're present at mount, so no enter
// class is applied). Progress mode always renders the full list.
const visibleItems = computed(() => {
  if (progressMode.value) return items.value
  if (!animEnabled.value) return items.value
  const floor = Math.min(animSkipCount.value, items.value.length)
  const limit = Math.max(revealCount.value, floor)
  return items.value.slice(0, limit)
})

function clearPending() {
  for (const t of pendingTimers) clearTimeout(t)
  pendingTimers = []
}

// ─── Progress-sequence mode ──────────────────────────────────────────────
const progressMode = computed(() => fields.value.progressMode === true)
// Two mutually-exclusive visual themes for progressMode, sharing the same
// itemProgress state machine below: 'ring' (default, unchanged) swaps a
// per-item circular ring for a checkmark; 'bar' fills a horizontal track and
// prints the live percentage beside the heading, no marker column.
const progressStyle = computed<ProgressStyle>(() => fields.value.progressStyle ?? 'ring')
const fillDurationMs = computed(() =>
  Math.max(0, Number(fields.value.progressFillDuration ?? 1200)),
)
const itemDelayMs = computed(() => Math.max(0, Number(fields.value.progressItemDelay ?? 200)))
const progressIconSizeCss = computed<string>(
  () => size(fields.value.progressIconSize, '32px') ?? '32px',
)
const progressStrokeWidthVal = computed(() => {
  const n = Number(fields.value.progressStrokeWidth ?? 16)
  return Number.isFinite(n) && n > 0 ? Math.min(100, n) : 16
})
const hasProgressSlot = computed(() => (props.slotChildren?.progressIcon?.length ?? 0) > 0)
const hasCheckmarkSlot = computed(() => (props.slotChildren?.checkmarkIcon?.length ?? 0) > 0)

// Per-item fill value (0..100). itemState derives the three render states.
const itemProgress = ref<number[]>([])
function itemState(i: number): IconListItemState {
  const p = itemProgress.value[i] ?? 0
  if (p >= 100) return 'done'
  if (p > 0) return 'filling'
  return 'pending'
}
function bodyVisible(i: number): boolean {
  return !progressMode.value || itemState(i) === 'done'
}
// The bar theme has its own "filling" indicator (the bar itself, mid-fill),
// so the body-skeleton shimmer would be a second, redundant pulsing element
// under it — suppressed there entirely rather than just while filling.
function bodySkeleton(i: number): boolean {
  return progressMode.value && progressStyle.value !== 'bar' && itemState(i) === 'filling'
}

// Bar-theme per-item state. At rest in the editor (not previewing/live)
// itemProgress isn't animating and itemState() would report every row
// 'done' (see settleProgressDone) — mirroring the ring theme's authoring
// view (a hardcoded 65%-filling ring on the first row), the first row is
// shown "filling" and the rest 'pending', so the author can see and style
// the active-vs-not-yet-reached look without clicking Preview Animation.
function barItemState(i: number): IconListItemState {
  if (!animLive.value) return i === 0 ? 'filling' : 'pending'
  return itemState(i)
}
// Bar-theme fill/percentage value (0..100), consistent with barItemState:
// 'filling' shows the demo/live partial value, 'done' is full, 'pending' is
// empty (so the pending row's bar reads as not-yet-reached, not just muted).
function barDisplayProgress(i: number): number {
  const state = barItemState(i)
  if (state === 'pending') return 0
  if (!animLive.value) return 65 // demo value for the 'filling' first row
  return Math.min(100, Math.max(0, itemProgress.value[i] ?? 0))
}

// Guards against the effectiveTrigger watcher (which fires when `previewing`
// flips) clobbering an in-flight sequence.
const progressActive = ref(false)
let progressRaf: number | null = null
let progressDelayTimer: ReturnType<typeof setTimeout> | null = null
let fillStartTs = 0
let fillingIndex = 0

function clearProgressTimers() {
  if (progressRaf !== null) cancelAnimationFrame(progressRaf)
  progressRaf = null
  if (progressDelayTimer !== null) clearTimeout(progressDelayTimer)
  progressDelayTimer = null
}

function settleProgressDone() {
  clearProgressTimers()
  progressActive.value = false
  itemProgress.value = items.value.map(() => 100)
}

function setAllPending() {
  clearProgressTimers()
  progressActive.value = false
  itemProgress.value = items.value.map(() => 0)
}

// Fills the current `fillingIndex` item 0→100 over progressFillDuration using
// the rAF timestamp (tab-throttle-safe: a backgrounded tab simply resumes and
// catches up), then advances after progressItemDelay.
function fillActive() {
  fillStartTs = 0
  const tick = (ts: number) => {
    if (fillStartTs === 0) fillStartTs = ts
    const dur = fillDurationMs.value
    const p = dur <= 0 ? 100 : Math.min(100, ((ts - fillStartTs) / dur) * 100)
    const arr = itemProgress.value.slice()
    arr[fillingIndex] = p
    itemProgress.value = arr
    if (p < 100) {
      progressRaf = requestAnimationFrame(tick)
      return
    }
    progressRaf = null
    if (fillingIndex < items.value.length - 1) {
      progressDelayTimer = setTimeout(() => {
        progressDelayTimer = null
        fillingIndex++
        fillActive()
      }, itemDelayMs.value)
    } else {
      // Sequence complete. In the editor, drop the preview flag after a beat so
      // the component returns to the authoring view (slot drop-zones).
      progressDelayTimer = setTimeout(
        () => {
          progressDelayTimer = null
          if (isEditor.value) previewing.value = false
        },
        Math.max(itemDelayMs.value, 600),
      )
    }
  }
  progressRaf = requestAnimationFrame(tick)
}

function runProgressSequence() {
  clearProgressTimers()
  previewing.value = true
  progressActive.value = true
  itemProgress.value = items.value.map(() => 0)
  if (items.value.length === 0) {
    progressActive.value = false
    return
  }
  fillingIndex = 0
  fillActive()
}

function startAnimation() {
  if (progressMode.value) {
    runProgressSequence()
    return
  }
  clearPending()
  previewing.value = true
  const count = items.value.length
  // Skip-count items render immediately with the component (no enter anim).
  // Start revealCount at `skip` rather than 0 so a replay of an already-
  // settled animation doesn't briefly hide the always-visible items.
  const skip = Math.min(animSkipCount.value, count)
  revealCount.value = skip
  // Let the initial opacity:0 frame paint before we flip each reveal.
  nextTick(() => {
    requestAnimationFrame(() => {
      for (let i = skip; i < count; i++) {
        const timer = setTimeout(
          () => {
            if (revealCount.value < i + 1) revealCount.value = i + 1
          },
          (i - skip) * animDelayMs.value,
        )
        pendingTimers.push(timer)
      }
      // After the last item has faded in, drop the preview flag. In editor
      // mode this returns the component to the "all items visible, no
      // animation" steady state; in live mode this is a no-op because
      // animLive stays true regardless.
      const settle = setTimeout(
        () => {
          previewing.value = false
        },
        Math.max(0, count - 1 - skip) * animDelayMs.value + animDurationMs.value,
      )
      pendingTimers.push(settle)
    })
  })
}

// Reset reveal/progress state whenever the effective trigger mode changes (also
// runs once on mount via onMounted below). For 'none' (includes editor mode
// while not previewing), show everything immediately. For 'mount' start a run;
// for 'event' stay idle until the processor fires.
function applyTriggerMode(mode: AnimationTrigger) {
  clearPending()
  if (progressMode.value) {
    if (mode === 'none') {
      // Editor at rest / runtime trigger=none → show the completed checklist.
      settleProgressDone()
    } else if (progressActive.value) {
      // A run is already in flight (started by startAnimation or a prior
      // mount apply). Don't restart or reset it from the previewing-flip watch.
      return
    } else if (mode === 'mount') {
      runProgressSequence()
    } else {
      // 'event' — rings idle at 0% until startAnimation fires.
      setAllPending()
    }
    return
  }
  clearProgressTimers()
  if (mode === 'none') {
    revealCount.value = items.value.length
  } else if (mode === 'mount') {
    startAnimation()
  } else {
    revealCount.value = 0
  }
}

watch(effectiveTrigger, applyTriggerMode)

// Toggling progress mode (or other animation flips) re-derives the resting
// state. Reset progressActive so applyTriggerMode can start fresh.
watch(progressMode, () => {
  progressActive.value = false
  applyTriggerMode(effectiveTrigger.value)
})

onMounted(() => {
  applyTriggerMode(effectiveTrigger.value)
})

// Keep state in sync with the list length. Without this, items added through
// the items editor while trigger='mount'/'event' stay hidden in the preview,
// and the progress array would be the wrong length.
watch(
  () => items.value.length,
  (len) => {
    if (progressMode.value) {
      progressActive.value = false
      applyTriggerMode(effectiveTrigger.value)
      return
    }
    if (effectiveTrigger.value === 'none') revealCount.value = len
  },
)

onBeforeUnmount(() => {
  clearPending()
  clearProgressTimers()
})

// Live event-wiring hook: a Button.onClick in the authored pipe can target
// IconList.startAnimation via the component-processor system.
useComponentProcessors({
  startAnimation: () => startAnimation(),
})

// Authoring-time hook: the "Preview Animation" button in the field editor
// fires this via the builder:editor-command bus. Kept separate from the
// processor so it doesn't show up in the user-facing event-wiring dropdown.
// Same name as the processor — the command verb is identical, the registries
// are separate.
useEditorCommands({
  startAnimation: () => startAnimation(),
})

function fontStyle(d: TextDecoration[] | undefined): string {
  return d?.includes('italic') ? 'italic' : 'normal'
}

function textDecoration(d: TextDecoration[] | undefined): string {
  const deco = (d ?? []).filter((v) => v === 'underline' || v === 'line-through')
  return deco.length ? deco.join(' ') : 'none'
}

const progressFill = computed(() =>
  color(fields.value.progressFillColor, 'var(--color-secondary-500)'),
)

// ─── Text-style presets ──────────────────────────────────────────────────
// Heading and body each resolve their five typography properties from a
// stylesheet text-style group, exactly like the Text component: the per-
// property override field wins, the preset is the fallback. 'custom' resolves
// to null so the property is left unset and inherits from the page.
type TextStyleProperty = 'family' | 'weight' | 'size' | 'line-height' | 'color'
function presetVar(preset: TextStylePreset, property: TextStyleProperty): string | null {
  return preset === 'custom' ? null : `var(--${preset}-${property})`
}
// Legacy content predates both fields; the defaults are the tokens the heading
// and body were previously hardcoded to, so an untouched pipe is unchanged.
const headingPreset = computed<TextStylePreset>(
  () => fields.value.headingTextStyle ?? 'text-heading-3',
)
const bodyPreset = computed<TextStylePreset>(() => fields.value.bodyTextStyle ?? 'text-body-md')
// The one property the body does not take from its preset while that preset is
// the default one: IconList's body has always been muted (--color-text-secondary,
// not text-body-md's own --color-text-primary), and honouring the preset there
// would silently recolour every existing list. Any other preset brings its
// colour along, which is what picking one is for.
const bodyPresetColor = computed(() =>
  bodyPreset.value === 'text-body-md'
    ? 'var(--color-text-secondary)'
    : presetVar(bodyPreset.value, 'color'),
)

const css = computed(() => ({
  iconWidth: size(fields.value.iconWidth, 'var(--space-64)'),
  // The numeric/symbol marker sits on the heading's first line, so it tracks
  // the heading's preset (and its resolved line height) rather than a preset
  // of its own.
  markerColor: color(fields.value.markerColor, presetVar(headingPreset.value, 'color')),
  markerFontSize: size(fields.value.markerFontSize, presetVar(headingPreset.value, 'size')),
  markerFontWeight: fields.value.markerFontWeight || presetVar(headingPreset.value, 'weight'),
  headingColor: color(fields.value.headingColor, presetVar(headingPreset.value, 'color')),
  headingFontFamily:
    fields.value.headingFontFamily?.trim() || presetVar(headingPreset.value, 'family'),
  headingFontSize: size(fields.value.headingFontSize, presetVar(headingPreset.value, 'size')),
  headingLineHeight: size(
    fields.value.headingLineHeight,
    presetVar(headingPreset.value, 'line-height'),
  ),
  headingFontWeight: fields.value.headingFontWeight || presetVar(headingPreset.value, 'weight'),
  headingFontStyle: fontStyle(fields.value.headingTextDecoration),
  headingTextDecoration: textDecoration(fields.value.headingTextDecoration),
  bodyColor: color(fields.value.bodyColor, bodyPresetColor.value),
  bodyFontFamily: fields.value.bodyFontFamily?.trim() || presetVar(bodyPreset.value, 'family'),
  bodyFontSize: size(fields.value.bodyFontSize, presetVar(bodyPreset.value, 'size')),
  bodyLineHeight: size(fields.value.bodyLineHeight, presetVar(bodyPreset.value, 'line-height')),
  bodyFontWeight: fields.value.bodyFontWeight || presetVar(bodyPreset.value, 'weight'),
  bodyFontStyle: fontStyle(fields.value.bodyTextDecoration),
  bodyTextDecoration: textDecoration(fields.value.bodyTextDecoration),
  margin: getSpacingStyleData(fields.value.margin, '0'),
  itemGap: size(fields.value.itemGap, 'var(--space-16)'),
  iconTextGap: size(fields.value.iconTextGap, 'var(--space-12)'),
  headingBodyGap: size(fields.value.headingBodyGap, 'var(--space-4)'),
  containerPadding: getSpacingStyleData(fields.value.containerPadding, 'var(--space-16)'),
  containerBg: color(fields.value.containerBgColor, 'var(--color-action-tertiary-background)'),
  containerBorderColor: color(
    fields.value.containerBorderColor,
    'var(--color-action-tertiary-border)',
  ),
  containerBorderWidth: size(fields.value.containerBorderWidth, 'var(--border-width)'),
  containerBorderRadius: size(fields.value.containerBorderRadius, 'var(--radius-md)'),
  animDuration: `${animDurationMs.value}ms`,
  progressTrack: color(fields.value.progressTrackColor, 'var(--color-secondary-200)'),
  progressFill: progressFill.value,
  checkmark: color(fields.value.checkmarkColor, progressFill.value),
  progressBarHeight: size(fields.value.progressBarHeight, '8px'),
  progressBarRadius: size(fields.value.progressBarRadius, '999px'),
  progressBarTrack: color(fields.value.progressBarTrackColor, 'var(--color-secondary-200)'),
  // Gradient wins over the solid color when it has ≥2 stops (same
  // color-then-gradient-image layering convention as PageBackground's
  // bgColor/bgGradient), so either is a valid, independently stylable fill.
  progressBarFill:
    getGradientStyleData(fields.value.progressBarFillGradient) ??
    color(fields.value.progressBarFillColor, 'var(--color-secondary-500)'),
  percentColor: color(fields.value.progressPercentColor, 'var(--text-heading-3-color)'),
  percentFontSize: size(fields.value.progressPercentFontSize, 'var(--text-body-md-size)'),
  percentFontWeight: fields.value.progressPercentFontWeight || 'var(--text-heading-3-weight)',
  pendingHeadingColor: color(
    fields.value.progressPendingHeadingColor,
    'var(--color-text-secondary)',
  ),
  // Unlike the color overrides above (which default to a distinct muted
  // token), the size/weight/track overrides default to whatever the active
  // row already uses — opt-in, so an untouched pipe renders byte-identical
  // to before these fields existed.
  pendingHeadingFontSize: size(
    fields.value.progressPendingHeadingFontSize,
    size(fields.value.headingFontSize, presetVar(headingPreset.value, 'size')),
  ),
  pendingHeadingFontWeight:
    fields.value.progressPendingHeadingFontWeight ||
    fields.value.headingFontWeight ||
    presetVar(headingPreset.value, 'weight'),
  pendingPercentColor: color(
    fields.value.progressPendingPercentColor,
    'var(--color-text-secondary)',
  ),
  pendingPercentFontSize: size(
    fields.value.progressPendingPercentFontSize,
    size(fields.value.progressPercentFontSize, 'var(--text-body-md-size)'),
  ),
  pendingPercentFontWeight:
    fields.value.progressPendingPercentFontWeight ||
    fields.value.progressPercentFontWeight ||
    'var(--text-heading-3-weight)',
  pendingBarTrack: color(fields.value.progressPendingBarTrackColor, 'var(--color-secondary-200)'),
  bodySkeletonBg: color(fields.value.bodySkeletonBgColor, '#e5e7eb'),
  bodySkeletonShimmer: color(fields.value.bodySkeletonShimmerColor, '#f3f4f6'),
}))
</script>

<style scoped lang="scss">
.icon-list {
  list-style: none;
  margin: v-bind('css.margin');
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: v-bind('css.itemGap');
  width: 100%;
}

// Per-item grid wrapper. The `1fr` row at rest lets the inner content occupy
// its natural height. For the slide enter animation we interpolate the row
// from `0fr` to `1fr`, which smoothly animates the collapsed height.
// `min-height: 0` is required so the row can actually shrink to 0.
.icon-list-row {
  display: grid;
  grid-template-rows: 1fr;
  min-height: 0;
  width: 100%;
}

.icon-list-item {
  display: flex;
  flex-direction: column;
  gap: v-bind('css.headingBodyGap');
  width: 100%;
  box-sizing: border-box;
  // Needed so the grid row can clip this box during the height transition —
  // without these the inner content would overflow the collapsing row.
  overflow: hidden;
  min-height: 0;

  &.wrapped {
    padding: v-bind('css.containerPadding');
    background-color: v-bind('css.containerBg');
    border-style: solid;
    border-color: v-bind('css.containerBorderColor');
    border-width: v-bind('css.containerBorderWidth');
    border-radius: v-bind('css.containerBorderRadius');
  }
}

.icon-heading-row {
  display: flex;
  gap: v-bind('css.iconTextGap');

  &.side-left {
    flex-direction: row;
  }
  &.side-right {
    flex-direction: row-reverse;
  }

  &.align-top {
    align-items: flex-start;
  }
  &.align-center {
    align-items: center;
  }
  &.align-bottom {
    align-items: flex-end;
  }
}

.icon-img {
  display: block;
  width: v-bind('css.iconWidth');
  height: auto;
  flex-shrink: 0;
}

.icon-marker {
  flex-shrink: 0;
  min-width: v-bind('css.iconWidth');
  text-align: center;
  color: v-bind('css.markerColor');
  font-size: v-bind('css.markerFontSize');
  font-family: v-bind('css.headingFontFamily');
  font-weight: v-bind('css.markerFontWeight');
  line-height: v-bind('css.headingLineHeight');
}

// Progress-sequence marker — sized to progressIconSize, centered in the row.
.progress-marker {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: v-bind(progressIconSizeCss);
  min-width: v-bind(progressIconSizeCss);
}

// Authoring view stacks the two override drop-zones side by side.
.progress-marker--authoring {
  width: auto;
  min-width: 0;
  gap: 8px;
}

.text-column {
  display: flex;
  flex-direction: column;
  gap: v-bind('css.headingBodyGap');
  flex: 1;
  min-width: 0;
}

.heading-text {
  color: v-bind('css.headingColor');
  font-size: v-bind('css.headingFontSize');
  font-family: v-bind('css.headingFontFamily');
  font-weight: v-bind('css.headingFontWeight');
  font-style: v-bind('css.headingFontStyle');
  text-decoration: v-bind('css.headingTextDecoration');
  line-height: v-bind('css.headingLineHeight');
  text-align: v-bind(headingTextAlign);
}

// Bar theme — heading + live percentage share a row; the bar fills below.
.heading-percent-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: v-bind('css.iconTextGap');
  width: 100%;

  > .heading-text {
    flex: 1;
    min-width: 0;
  }

  // Rows the sequence hasn't reached yet — muted so the active/completed
  // row(s) read as the ones "in progress". The track's own pending color is
  // applied inline per-row (see the template) since it needs the same
  // per-item barItemState() check the fill width already uses.
  &.is-pending {
    > .heading-text {
      color: v-bind('css.pendingHeadingColor');
      font-size: v-bind('css.pendingHeadingFontSize');
      font-weight: v-bind('css.pendingHeadingFontWeight');
    }
    > .progress-percent {
      color: v-bind('css.pendingPercentColor');
      font-size: v-bind('css.pendingPercentFontSize');
      font-weight: v-bind('css.pendingPercentFontWeight');
    }
  }
}

.progress-percent {
  flex-shrink: 0;
  color: v-bind('css.percentColor');
  font-size: v-bind('css.percentFontSize');
  font-family: var(--text-body-md-family);
  font-weight: v-bind('css.percentFontWeight');
  line-height: var(--text-body-md-line-height);
}

.progress-bar-track {
  width: 100%;
  height: v-bind('css.progressBarHeight');
  border-radius: v-bind('css.progressBarRadius');
  // background-color is set inline per-row (active vs. pending track color).
  overflow: hidden;
}

// No width transition — itemProgress already updates every animation frame
// (see fillActive's rAF loop), so a CSS transition here would fight it
// exactly like ProgressRingShape's dash-offset stays untransitioned too.
.progress-bar-fill {
  height: 100%;
  border-radius: inherit;
}

.body-text {
  color: v-bind('css.bodyColor');
  font-size: v-bind('css.bodyFontSize');
  font-family: v-bind('css.bodyFontFamily');
  font-weight: v-bind('css.bodyFontWeight');
  font-style: v-bind('css.bodyFontStyle');
  text-decoration: v-bind('css.bodyTextDecoration');
  line-height: v-bind('css.bodyLineHeight');
  text-align: v-bind(bodyTextAlign);
}

// Body skeleton shown while an item is filling (mirrors PlainImage shimmer).
.body-skeleton {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  padding: 2px 0;

  &.body-below {
    margin-top: v-bind('css.headingBodyGap');
  }

  .skeleton-line {
    display: block;
    height: 0.7em;
    border-radius: 4px;
    background-color: v-bind('css.bodySkeletonBg');
    background-image: linear-gradient(
      90deg,
      transparent 0%,
      v-bind('css.bodySkeletonShimmer') 50%,
      transparent 100%
    );
    background-size: 200% 100%;
    background-repeat: no-repeat;
    animation: icon-list-skeleton-shimmer 1.4s linear infinite;

    &.short {
      width: 60%;
    }
  }
}

@keyframes icon-list-skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

// TransitionGroup enter animations — only active when :css=true (animation
// running live or previewing in editor). The enter classes land on the outer
// `<li class="icon-list-row">` that TransitionGroup transitions; the visual
// effects target either the row itself (for height) or the inner
// `.icon-list-item` (for opacity / translate).

// Fade: opacity-only on the inner content. The row keeps its natural height
// as soon as it enters the DOM, so the list grows by one item-height per
// step (instant per-item, smooth visual via opacity).
.icon-list-fade-enter-from > .icon-list-item {
  opacity: 0;
}
.icon-list-fade-enter-active > .icon-list-item {
  transition: opacity v-bind('css.animDuration') ease;
}

// Slide-in: the inner item starts one item-height above its resting position
// AND the outer row starts collapsed (0fr), so the list container's height
// animates alongside the slide rather than snapping up. The grid row
// interpolates from 0fr to 1fr, which resolves to the inner content's
// natural height — works with variable item heights without measurement.
.icon-list-slide-enter-from {
  grid-template-rows: 0fr;
}
.icon-list-slide-enter-from > .icon-list-item {
  opacity: 0;
  transform: translateY(-100%);
}
.icon-list-slide-enter-active {
  transition: grid-template-rows v-bind('css.animDuration') ease;
}
.icon-list-slide-enter-active > .icon-list-item {
  transition:
    opacity v-bind('css.animDuration') ease,
    transform v-bind('css.animDuration') ease;
}
</style>
